/** A title, a pill row that answers itself, and a rail illustrating the pick. */
import { useShelfSelection } from './shelf';
import { Surface, Vertical } from './primitives';
import { PanelHeader } from './panelHeader';
import { ItemCollection } from './collections';

const PILL = {
  minHeight: 34,
  borderRadius: 20,
  borderWidth: 1,
  padding: '0 16px',
  fontSize: 13,
  fontWeight: 600,
  // A pill label is blueprint-supplied: keep every pill on one line and let
  // the row scroll. A minimum height also leaves room for larger text.
  whiteSpace: 'nowrap',
  display: 'inline-flex',
  alignItems: 'center',
  flex: '0 0 auto',
  cursor: 'pointer',
  background: 'none',
};

const PARAGRAPH = {
  fontSize: 14,
  lineHeight: 1.6,
  color: 'var(--text-2)',
};

const optionsOf = (props) => (Array.isArray(props.options) ? props.options : []);

export function AnswerShelf({ nodeId, node, props = {}, data, ctx, emit, slots, renderTemplate }) {
  const options = optionsOf(props);
  const [active, setActive] = useShelfSelection(nodeId, props, slots, options);

  const pick = (key) => {
    setActive(key);
    emit?.('modeChanged', { key });
  };

  const activeOption = options.find((o) => String(o.key) === String(active));
  const caption = activeOption?.caption ?? props.caption ?? '';

  return (
    <Surface variant="card" props={{ padding: '4', radius: 28 }}>
      <Vertical props={{ gap: '3' }}>
        <PanelHeader
          title={props.title}
          action={props.action}
          onAction={() => emit?.('actionActivated', {
            action: props.action?.action || props.action?.label || null,
          })}
        />
        {options.length ? (
          <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 8 }}>
            {options.map((o) => {
              const on = String(o.key) === String(active);
              return (
                <button
                  key={o.key}
                  type="button"
                  aria-pressed={on}
                  onClick={() => pick(o.key)}
                  style={{
                    ...PILL,
                    // Selected fills with the page's ink; the rest stay
                    // TRANSPARENT with a hairline — never a filled surface —
                    // so the row reads as unpicked, not merely dim.
                    background: on ? 'var(--text)' : 'transparent',
                    color: on ? 'var(--surface)' : 'var(--text-3)',
                    border: `1px solid ${on ? 'var(--text)' : 'var(--line)'}`,
                  }}
                >
                  {o.label ?? o.key}
                </button>
              );
            })}
          </div>
        ) : null}
        {caption ? <div style={PARAGRAPH}>{caption}</div> : null}
        <ItemCollection
          nodeId={nodeId}
          node={node}
          props={{
            layout: 'rail',
            pick: active,
            width: props.width ?? 110,
            aspect: props.aspect ?? '1/1',
            gap: '3',
            items: props.items,
            card: props.card,
            pending: props.pending,
            crud: props.crud,
            pendingScope: props.pendingScope,
            filterField: props.filterField,
            emptyTitle: props.emptyTitle,
            emptyBody: props.emptyBody,
          }}
          data={data}
          ctx={ctx}
          emit={emit}
          slots={{ ...slots, applyGroup: undefined }}
          renderTemplate={renderTemplate || props.renderTemplate}
        />
      </Vertical>
    </Surface>
  );
}

export default AnswerShelf;
